import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { UserPlus, X } from 'lucide-react'
import { createCrmStaff, CrmRequestError, STAFF_ADDED_EVENT, type StaffMember } from '../leads/leadCrmClient'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

type FieldError = { field: 'displayName' | 'email' | null; message: string }

/**
 * Adds an active crm_staff row. Any signed-in CRM user can do this: the CRM
 * has one shared session and no admin role. Shared by Quick Actions and the
 * Sales Team page; a created salesperson is broadcast so any open view adds it.
 */
export function AddSalespersonModal({
  onClose,
  onAdded,
}: {
  onClose: () => void
  onAdded?: (staff: StaffMember) => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<FieldError | null>(null)
  const [closing, setClosing] = useState(false)
  const closeTimerRef = useRef<number | null>(null)
  const id = useId()

  const nameInput = useCallback((node: HTMLInputElement | null) => node?.focus(), [])

  const requestClose = useCallback(() => {
    if (closeTimerRef.current !== null) return
    setClosing(true)
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    closeTimerRef.current = window.setTimeout(onClose, reduce ? 0 : 120)
  }, [onClose])

  useEffect(() => () => {
    if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
  }, [])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) requestClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [requestClose, saving])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError({ field: 'displayName', message: 'Enter a name.' })
      return
    }
    if (email.trim() && !EMAIL_PATTERN.test(email.trim())) {
      setError({ field: 'email', message: 'Enter a valid email address.' })
      return
    }

    setSaving(true)
    try {
      const staff = await createCrmStaff({ displayName: name.trim(), email: email.trim() })
      window.dispatchEvent(new CustomEvent<StaffMember>(STAFF_ADDED_EVENT, { detail: staff }))
      onAdded?.(staff)
      requestClose()
    } catch (cause) {
      // The entered values stay in the form.
      const field = cause instanceof CrmRequestError && (cause.field === 'displayName' || cause.field === 'email') ? cause.field : null
      setError({ field, message: cause instanceof Error ? cause.message : 'The salesperson could not be added.' })
      setSaving(false)
    }
  }

  const describe = (field: FieldError['field']) =>
    error?.field === field ? { 'aria-invalid': true, 'aria-describedby': `${id}-error` } : {}

  return createPortal(
    <div className={`lead-modal-layer lead-crm-layer staff-dialog-layer${closing ? ' is-closing' : ''}`}>
      <button aria-label="Close add salesperson" className="lead-modal-backdrop" onClick={requestClose} type="button" />
      <section
        aria-describedby={`${id}-description`}
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="lead-modal lead-crm-dialog staff-dialog"
        role="dialog"
      >
        <header className="staff-dialog-header">
          <span aria-hidden="true" className="staff-dialog-mark">
            <UserPlus size={18} strokeWidth={1.8} />
          </span>
          <div className="staff-dialog-heading">
            <h2 id={`${id}-title`}>Add Salesperson</h2>
            <p id={`${id}-description`}>Add a member to your sales team.</p>
          </div>
          <button aria-label="Close add salesperson" className="icon-button lead-modal-close" onClick={requestClose} type="button">
            <X size={19} />
          </button>
        </header>

        <form className="staff-dialog-body" noValidate onSubmit={submit}>
          <label className="staff-field" htmlFor={`${id}-name`}>
            <span>Name</span>
            <input
              {...describe('displayName')}
              autoComplete="off"
              disabled={saving}
              id={`${id}-name`}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Anthony Reyes"
              ref={nameInput}
              required
              value={name}
            />
          </label>

          <label className="staff-field" htmlFor={`${id}-email`}>
            <span>
              Email <em>(optional)</em>
            </span>
            <input
              {...describe('email')}
              autoComplete="off"
              disabled={saving}
              id={`${id}-email`}
              maxLength={254}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.com"
              type="email"
              value={email}
            />
          </label>

          {error ? <p className="staff-dialog-error" id={`${id}-error`} role="alert">{error.message}</p> : null}

          <div className="staff-dialog-actions">
            <button className="lead-crm-action" disabled={saving} onClick={requestClose} type="button">
              Cancel
            </button>
            <button className="button button-primary" disabled={saving} type="submit">
              {saving ? 'Adding...' : 'Add Salesperson'}
            </button>
          </div>
        </form>
      </section>
    </div>,
    document.body,
  )
}
